import { Broker, GUID } from '@c4/core';
import { Observable } from 'rxjs/Observable';
import { Injectable, Pipe } from '@angular/core';
import { Store } from '@ngrx/store';

import * as moment from 'moment';

import { CommonProgrammingContext } from '../../../common/services/shared-programming.service';
import { ProgrammingUtilsService as Utils, TAG_QSP } from './../../../common/services/utils.service';

import { MatchingSearchTextPipe } from './../../../common/pipes/matching-search-text.pipe';
import { ScheduleEvent } from './schedule.service';
import { Event } from './../../../common/interfaces/event.interface';

import { EnddateLabelPipe } from './../pipes/enddate-label.pipe';
import { TimeLabelPipe } from '../pipes/time-label.pipe';
import { StartdateLabelPipe } from '../pipes/startdate-label.pipe';
import { RepeatLabelPipe } from '../pipes/repeat-label.pipe';
import { SortSchedulesPipe } from '../../../common/pipes/sort-schedules';
import { HappeningTodayPipe } from '../../../common/pipes/happening-today.pipe';
import { deviceIconsReducer } from '../../../common/services/device-icons.service';
import { CodeItem } from '../../../common/interfaces/code-item.interface';
import { ScheduleLabelPipe } from '../../../common/pipes/schedule-label.pipe';
import { CW_CREATOR_ID } from '../../../common/services/utils.service';

export interface ScheduleEvent extends Event {
  buttonbindingid: number;
  creatorid: number;
  creatorState: string;
  hidden: boolean;
  display: string;
  subtitle?: string;
  start: {
    offset: number;
    offset_minutes: number;
    start_date: {
      start_day: number;
      start_month: number;
      start_year: number;
      start_period: number;
      start_weekday: number;
    }
    randomize: number;
  };
  next_occurence?: {
    year: number;
    month: number;
    day: number;
    hour: number;
    min: number;
    timezone: string;
  };
  repeat?: {
    rate: number;
    frequency: number;
    daymask: number;
    end_date?: {
      end_year: number;
      end_month: number;
      end_day: number;
    };
  };
  command?: any;
}

export interface ScheduleQuickstartContext {
  scheduleEvents: Array<ScheduleEvent>;
  initialized: boolean;
  ready: boolean;
  todaysEvents: Array<{
    time: number;
    events: ScheduleEvent[]
  }>;
  name: string;
  validationErrors: Array<{
    key: string;
    text: string;
  }>;
  /**
   * 0 = at, 1 = before, 2 = after
   * @type {number}
   */
  sunriseSunsetOffsetType: number;
  /**
   * 1 = sunrise, 2 = sunset
   * @type {number}
   */
  sunriseOrSunset: number;
  /**
   * number of minutes relative to sunrise/sunset (abs val)
   * ## minutes before/after sunset/sunset
   * @type {number}
   */
  sunriseSunsetOffsetInterval: number;
  /**
   * date input representation of a fixed start date
   * YYYY-MM-DD
   * @type {string}
   */
  startDate: string;
  /**
   * indicates whether the selected start date is fixed or relative
   * @type {boolean}
   */
  isFixedDate: boolean;
  /**
   * pseudo data for indicating whether a schedule repeats or not
   * (once | every)
   */
  repeatType: string;

  startOffsetMinutes: number;

  isFixedTime: boolean;
  startTime: string;

  startDayOfWeek: number;
  startMonth: number;
  startYear: number;
  startWeekOffset: number;
  endDate: string;
  hasEndDate: boolean;
  isRepeating: boolean;
  repeatRate: number;
  repeatFrequency: number;
  repeatDays: number;
  isRandomized: boolean;
  randomizeBy: number;
  done: boolean;

  eventId: number;
}

const DEFAULT_REPEAT: any = {
  repeatFrequency: 1,
  isRepeating: true,
  repeatRate: 1
};

const BLANK_EVENT: ScheduleEvent = {
  eventId: 0,
  deviceId: 100100,
  buttonbindingid: 0,
  creatorid: 0,
  creatorState: '',
  hidden: false,
  display: '',
  start: {
    offset: 0,
    offset_minutes: 0,
    start_date: {
      start_year: new Date().getFullYear(),
      start_month: new Date().getMonth(),
      start_day: new Date().getDate(),
      start_period: 0,
      start_weekday: 0
    },
    randomize: 0
  },
  repeat: {
    rate: 1,
    frequency: 1,
    daymask: 0
  }
};

const INITIAL_STATE: ScheduleQuickstartContext = {
  scheduleEvents: [],
  initialized: true,
  ready: true,
  todaysEvents: [],
  name: '',
  validationErrors: [],
  repeatType: 'every',
  isFixedTime: true,
  sunriseSunsetOffsetType: 0,
  sunriseSunsetOffsetInterval: 15,
  sunriseOrSunset: 1,
  startTime: moment().format('HH:mm'),
  startOffsetMinutes: 0,
  startDate: moment().format('YYYY-MM-DD'),
  isFixedDate: true,
  startDayOfWeek: 0,
  startMonth: 0,
  startYear: new Date().getFullYear(),
  startWeekOffset: 0,
  endDate: moment().format('YYYY-MM-DD'),
  hasEndDate: false,
  isRepeating: false,
  repeatRate: 1,
  repeatFrequency: 1,
  repeatDays: 0,
  isRandomized: false,
  randomizeBy: 0,
  done: false,
  eventId: undefined,
};

const CLEAR_SCHEDULE_STATE: any = {
  validationErrors: [],
  repeatType: 'every',
  isFixedTime: true,
  sunriseSunsetOffsetType: 0,
  sunriseSunsetOffsetInterval: 15,
  sunriseOrSunset: 1,
  startTime: moment().format('HH:mm'),
  startOffsetMinutes: 0,
  startDate: moment().format('YYYY-MM-DD'),
  isFixedDate: true,
  startDayOfWeek: 0,
  startMonth: 0,
  startYear: new Date().getFullYear(),
  startWeekOffset: 0,
  endDate: moment().format('YYYY-MM-DD'),
  hasEndDate: false,
  isRepeating: false,
  repeatRate: 1,
  repeatFrequency: 1,
  repeatDays: 0,
  isRandomized: false,
  randomizeBy: 0,
  done: false,
  eventId: undefined,
};

const STORE_NAME: string = 'PROGRAMMING:QUICKSTARTS:SCHEDULE:';
const SET_REPEAT_INTERVAL: string = STORE_NAME + 'SET_REPEAT_INTERVAL';
const SET_REPEAT_RATE: string = STORE_NAME + 'SET_REPEAT_RATE';
const SET_REPEAT_DAYS: string = STORE_NAME + 'SET_REPEAT_DAYS';
const SET_RANDOMIZE: string = STORE_NAME + 'SET_RANDOMIZE';
const SET_IS_RANDOMIZED: string = STORE_NAME + 'SET_IS_RANDOMIZED';
const SET_START_DATE: string = STORE_NAME + 'SET_START_DATE';
const SET_START_TIME: string = STORE_NAME + 'SET_START_TIME';
const SET_START_OFFSET_MINUTES: string = STORE_NAME + 'SET_START_OFFSET_MINUTES';
const SET_DEFAULT_START: string = STORE_NAME + 'SET_DEFAULT_START';
const SET_SUNRISE_SUNSET_OFFSET_TYPE: string = STORE_NAME + 'SET_SUNRISE_SUNSET_OFFSET_TYPE';
const SET_SUNRISE_SUNSET_OFFSET_INTERVAL: string = STORE_NAME + 'SET_SUNRISE_SUNSET_OFFSET_INTERVAL';
const SET_IS_FIXED_TIME: string = STORE_NAME + 'SET_IS_FIXED_TIME';
const SET_SUNRISE_OR_SUNSET: string = STORE_NAME + 'SET_SUNRISE_OR_SUNSET';

const SET_OFFSET_INTERVAL: string = STORE_NAME + 'SET_OFFSET_INTERVAL';
const SET_IS_FIXED_DATE: string = STORE_NAME + 'SET_IS_FIXED_DATE';
const TOGGLE_HAS_END_DATE: string = STORE_NAME + 'TOGGLE_HAS_END_DATE';
const SET_END_DATE: string = STORE_NAME + 'SET_END_DATE';
const SET_WEEK_OFFSET: string = STORE_NAME + 'SET_WEEK_OFFSET';
const SET_START_DAY_OF_WEEK: string = STORE_NAME + 'SET_START_DAY_OF_WEEK';
const SET_START_MONTH: string = STORE_NAME + 'SET_START_MONTH';
const SET_START_YEAR: string = STORE_NAME + 'SET_START_YEAR';
const SET_INITIAL_STATE: string = STORE_NAME + 'SET_INITIAL_STATE';
const SET_REPEAT_TYPE: string = STORE_NAME + 'SET_REPEAT_TYPE';
const SET_REPEAT: string = STORE_NAME + 'SET_REPEAT';
const SET_NAME: string = STORE_NAME + 'SET_NAME';
const SET_SCHEDULE_EVENTS: string = STORE_NAME + 'SET_SCHEDULE_EVENTS';
const SET_TODAYS_EVENTS: string = STORE_NAME + 'SET_TODAYS_EVENTS';
const SET_INITIALIZED: string = STORE_NAME + 'SET_INITIALIZED';
const CLEAR_SCHEDULE: string = STORE_NAME + 'CLEAR_SCHEDULE';
const SET_READY: string = STORE_NAME + 'SET_READY';

const SET_VALIDATION_ERRORS: string = STORE_NAME + 'SET_VALIDATION_ERRORS';

export function scheduleQuickstartReducer(state: ScheduleQuickstartContext = INITIAL_STATE, { type, payload }) {
  if (type.indexOf(STORE_NAME) > -1) {
    // console.log(type, payload);
  }

  switch (type) {
    case SET_READY: return Object.assign({}, state, { ready: payload });

    case SET_REPEAT_INTERVAL: return Object.assign({}, state, { repeatFrequency: payload });
    case SET_REPEAT_DAYS: return Object.assign({}, state, { repeatDays: payload });
    case SET_REPEAT_RATE: return Object.assign({}, state, { repeatRate: payload });
    case SET_RANDOMIZE: return Object.assign({}, state, { randomizeBy: payload });
    case SET_IS_RANDOMIZED: return Object.assign({}, state, { isRandomized: payload });
    case SET_REPEAT_TYPE: return Object.assign({}, state, { repeatType: payload });
    case SET_REPEAT: return Object.assign({}, state, payload);
    case SET_INITIAL_STATE: return Object.assign({}, INITIAL_STATE, payload);
    case SET_START_OFFSET_MINUTES: return Object.assign({}, state, { startOffsetMinutes: payload });

    case SET_START_TIME: return Object.assign({}, state, { startTime: payload });
    case SET_START_DATE: return Object.assign({}, state, { startDate: payload });
    case SET_DEFAULT_START: return Object.assign({}, state, payload);
    case SET_IS_FIXED_TIME: return Object.assign({}, state, { isFixedTime: payload });
    case SET_SUNRISE_SUNSET_OFFSET_TYPE: return Object.assign({}, state, { sunriseSunsetOffsetType: payload });
    case SET_SUNRISE_SUNSET_OFFSET_INTERVAL: return Object.assign({}, state, { sunriseSunsetOffsetInterval: payload });
    case SET_SUNRISE_OR_SUNSET: return Object.assign({}, state, { sunriseOrSunset: payload });
    case SET_IS_FIXED_DATE: return Object.assign({}, state, { isFixedDate: payload });
    case SET_END_DATE: return Object.assign({}, state, { endDate: payload });
    case SET_WEEK_OFFSET: return Object.assign({}, state, { startWeekOffset: payload });
    case SET_START_DAY_OF_WEEK: return Object.assign({}, state, { startDayOfWeek: payload });
    case SET_START_MONTH: return Object.assign({}, state, { startMonth: payload });
    case SET_START_YEAR: return Object.assign({}, state, { startYear: payload });
    case TOGGLE_HAS_END_DATE: return Object.assign({}, state, { hasEndDate: !state.hasEndDate });
    case SET_VALIDATION_ERRORS: return Object.assign({}, state, { validationErrors: payload });
    case SET_NAME: return Object.assign({}, state, { name: payload });
    case SET_SCHEDULE_EVENTS: return Object.assign({}, state, { scheduleEvents: payload });
    case SET_TODAYS_EVENTS: return Object.assign({}, state, { todaysEvents: payload });
    case SET_INITIALIZED: return Object.assign({}, state, { initialized: payload, ready: payload });

    case CLEAR_SCHEDULE: return Object.assign({}, state, CLEAR_SCHEDULE_STATE);

    default: return state;
  }
};

/**
 * Broker routes summary for scheduing
 *
 * create a new schedule event, with or without code items
 * POST /api/v1/agents/scheduler
 *
 * delete a schedule event (and it's code items)
 * DELETE /api/v1/agents/scheduler/:eventId
 *
 * update the definition of an existing schedule
 * POST /api/v1/agents/scheduler/:eventId
 *
 * add a new code item to an existing
 * POST /api/v1/agents/scheduler/:eventId/commands|conditionals|loops
 *
 * delete a code item on a schedule event
 * DELETE /api/v1/agents/scheduler/:eventId/:codeitemId
 *
 * update a code item on a schedule event
 * POST /api/v1/agents/scheduler/:eventId/:codeitemId
 *
 * invoke (test) the programming attached to a schedule event
 * PUT /api/v1/agents/scheduler/:eventId
 *
 */

@Injectable()
export class ScheduleProgrammingService {

  scheduleEvents: Observable<ScheduleEvent[]>;
  initialized: Observable<boolean>;
  trigger: Observable<any>;
  selected: boolean;
  ready: Observable<boolean>;

  private _scheduleEventsObservable: Observable<ScheduleEvent[]>;

  constructor(
    protected store: Store<{
      scheduleProgramming: ScheduleQuickstartContext,
      sharedProgramming: CommonProgrammingContext
    }>,
    protected scheduleLabel: ScheduleLabelPipe,
    protected utils: Utils,
    protected broker: Broker
  ) {
    this.selected = false;
    this.trigger = this.store.select(s => s.sharedProgramming);
    this.scheduleEvents = this.store.select(s => s.scheduleProgramming.scheduleEvents);
    this.initialized = this.store.select(s => s.scheduleProgramming.initialized);

    this.store.select(s => s.sharedProgramming.initialized).subscribe(initialized => {
      if (initialized) {
        this.refresh();
      }
    });

    this.ready = this.store.select(s => s.scheduleProgramming.ready);
  }

  refresh(): Promise<any> {
    this.store.dispatch({ type: SET_INITIALIZED, payload: false });
    return new Promise<any>((resolve, reject) => {
      this.broker.call({
        path: '/api/v1/agents/scheduler',
        queryString: {
          // NOTE filter the driver auto update schedule
          query: { $not: { creatorId: 100124 } }
        }
      }).then(events => {
        // console.log('get schedule events response:', events);
        // events = this.utils.addIcons(events);
        events = this.utils.resolveProtected(events);
        events.forEach(evt => evt.subtitle = this.scheduleLabel.transform(evt));
        this.store.dispatch({ type: SET_SCHEDULE_EVENTS, payload: events });
        this.store.dispatch({ type: SET_INITIALIZED, payload: true });
        resolve(events);
      }, err => {
        this.store.dispatch({ type: SET_INITIALIZED, payload: true });
        reject(err);
      });
    });
  }

  clearSchedule() {
    this.store.dispatch({ type: CLEAR_SCHEDULE });
  }

  setInitialState(state: any) {
    this.selected = false;
    this.store.dispatch({ type: SET_INITIAL_STATE, payload: state });
  }

  setRepeatInterval(interval: any) {
    console.log('service: setRepeatInterval');
    this.store.dispatch({ type: SET_REPEAT_INTERVAL, payload: parseInt(interval) });
  }

  setRepeatRate(rate: any) {
    console.log('service: setRepeatRate');
    this.store.dispatch({ type: SET_REPEAT_RATE, payload: parseInt(rate) });
  }

  toggleRepeatDayOfWeek(dow: number) {
    console.log('service: toggleRepeatDayOfWeek');
    let num = Utils.snapshot(this.store.select(s => s.scheduleProgramming.repeatDays));
    let val = this.utils.bitFlip(num, dow);
    this.store.dispatch({ type: SET_REPEAT_DAYS, payload: val });
  }

  batchSelect(days: number) {
    console.log('service: batchSelect');
    this.store.dispatch({ type: SET_REPEAT_DAYS, payload: days });
  }

  /**
   *
   * @param {string} inputValue a time in the format of HH:MM (24-hour time)
   *
   * @memberOf ScheduleProgrammingService
   */
  setStartTime(inputValue: string) {
    console.log('service: setStartTime', inputValue);
    this.store.dispatch({ type: SET_START_TIME, payload: inputValue });

    // // HH:MM:SS
    // let parts = inputValue.split(':');
    // let minutes = parseInt(parts[0]) * 60;
    // minutes += parseInt(parts[1]);

    // console.log('service: offset minutes is', minutes);
    // this.store.dispatch({ type: SET_OFFSET_MINUTES, payload: minutes });
  }

  setStartOffsetMinutes(value: any) {
    let offset = parseInt(value);
    offset = ((offset < 0 || offset > 2) ? 0 : offset);
    if (offset) {
      this.store.dispatch({ type: SET_START_OFFSET_MINUTES, payload: offset });
    }
  }

  setRandomizeBy(value: any) {
    console.log('service: setRandomizeBy');
    let randMin = parseInt(value);
    if (!isNaN(randMin)) {
      this.store.dispatch({ type: SET_RANDOMIZE, payload: randMin });
    }
  }

  toggleRandomizeBy() {
    console.log('service: toggleRandomizeBy');
    let value = Utils.snapshot(this.store.select(s => s.scheduleProgramming.isRandomized));
    this.setIsRandomized(!value);
    // a little reverse logic here, if was previously randomized, set zero, if not, set 15 (reasonable default)
    this.setRandomizeBy(value ? 0 : 15);
  }

  setIsFixedStartTime(isFixed: boolean) {
    console.log('service: setFixedStart');
    this.store.dispatch({ type: SET_IS_FIXED_TIME, payload: isFixed });

    if (isFixed) {
      let now = new Date();
      let mins = (now.getHours() * 60) + now.getMinutes();
      this.store.dispatch({ type: SET_START_OFFSET_MINUTES, payload: mins });
    } else {
      this.setSunriseSunset(0);
    }
  }

  setSunriseSunset(value: any) {
    let sstype = parseInt(value);
    if (!isNaN(sstype)) {
      // clear fixed start offset minutes
      this.setStartOffsetMinutes(0);
      // set a reasonable start offset relative to sunrise/sunset
      this.setSunriseSunsetOffsetInterval(15);
    }
  }

  setSunriseSunsetOffsetType(type: any) {
    console.log('service: setSunriseSunsetOffsetType', type);
    let offset = parseInt(type);
    if (!isNaN(offset)) {
      offset = (offset < 0 || offset > 2) ? 0 : offset;
      this.store.dispatch({ type: SET_SUNRISE_SUNSET_OFFSET_TYPE, payload: offset });
    }
  }

  setSunriseSunsetOffsetInterval(value: any) {
    let interval = parseInt(value);
    if (!isNaN(interval)) {
      this.store.dispatch({ type: SET_SUNRISE_SUNSET_OFFSET_INTERVAL, payload: interval });
    }
  }

  setDefaultStart() {
    console.log('service: setDefaultStart');
    let now = new Date();

    let defaultStart: any = {
      startOffsetMinutes: (now.getHours() * 60 + now.getMinutes()),
      offsetType: 0,
      startMonth: 0,
      startYear: 0,
      startDay: 0,
      startDate: moment().format('YYYY-MM-DD'),
      startDayOfWeek: 0
    };

    this.store.dispatch({ type: SET_DEFAULT_START, payload: defaultStart });
  }

  setSunriseSunsetRelativeTo(value: any) {
    let relativeTo = parseInt(value);
    if (!isNaN(relativeTo)) {
      this.store.dispatch({ type: SET_SUNRISE_OR_SUNSET, payload: relativeTo });
    }
  }

  selectFixedDate(value: boolean) {
    this.store.dispatch({ type: SET_IS_FIXED_DATE, payload: value });
  }

  setFixedStartDate(value: any) {
    this.store.dispatch({ type: SET_START_DATE, payload: value });
  }

  setWeekOffset(value: any) {
    let offset = parseInt(value);
    if (!isNaN(offset)) {
      offset = (offset < 0 || offset > 4) ? 0 : offset;
      this.store.dispatch({ type: SET_WEEK_OFFSET, payload: offset });
    }
  }

  setStartDayOfWeek(value: any) {
    let day = parseInt(value);
    if (!isNaN(day)) {
      day = (day < 0 || day > 6) ? 0 : day;
      this.store.dispatch({ type: SET_START_DAY_OF_WEEK, payload: day });
    }
  }

  setStartMonth(value: any) {
    let month = parseInt(value);
    if (!isNaN(month)) {
      month = (month < 0 || month > 11) ? 0 : month;
      this.store.dispatch({ type: SET_START_MONTH, payload: month });
    }
  }

  setStartYear(value: any) {
    let year = parseInt(value);
    if (!isNaN(year)) {
      this.store.dispatch({ type: SET_START_YEAR, payload: year });
    }
  }

  setEndDate(value: any) {
    this.store.dispatch({ type: SET_END_DATE, payload: value });
  }

  toggleHasEndDate() {
    this.store.dispatch({ type: TOGGLE_HAS_END_DATE });
  }

  setIsRandomized(value: any) {
    let randomized = <boolean>(value);
    this.store.dispatch({ type: SET_IS_RANDOMIZED, payload: randomized });
    let randomizeBy = Utils.snapshot(this.store.select(s => s.scheduleProgramming.randomizeBy));
    if (!randomizeBy) {
      this.store.dispatch({ type: SET_RANDOMIZE, payload: 15 });
    }
  }

  setRepeatType(type: any) {
    this.store.dispatch({ type: SET_REPEAT_TYPE, payload: type });
    if (type !== 'once') {
      this.store.dispatch({ type: SET_REPEAT, payload: DEFAULT_REPEAT });
    }
  }

  refreshEvent(eventId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (eventId) {
        this.broker.call({
          path: '/api/v1/agents/scheduler/' + eventId
        }).then(res => {
          console.log('sched: response from refresh event', res);
          // this.utils.addIcons([res]);
          this.utils.resolveProtected([res]);
          res.subtitle = this.scheduleLabel.transform(res);
          resolve(res);
        }, err => {
          console.error('sched: error refreshing trigger', err);
          reject(err);
        });
      }
    });
  }

  setName(name: string) {
    this.store.dispatch({ type: SET_NAME, payload: name });
  }

  validate(): boolean {
    let context: ScheduleQuickstartContext = Utils.snapshot<ScheduleQuickstartContext>(this.store.select(s => s.scheduleProgramming));

    let errors = [];

    if (!context.name || context.name.length === 0) {
      errors.push({ key: 'name', text: 'A schedule must be given a name.' });
    }

    if (context.isFixedDate) {

    } else {

    }

    if (context.isFixedTime) {

    } else {

    }

    console.log('schedule: validation errors', errors);
    this.store.dispatch({ type: SET_VALIDATION_ERRORS, payload: errors });
    return (errors.length === 0);
  }

  saveAction(event: any, action: any): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log('schedule: saveaction: ', event, action);
      if (event && event.deviceId) {
        let path = undefined;
        let data = undefined;
        action.creatorState = this.utils.buildCreatorState(event, action);

        if (event.eventId) {
          // we must be updating an existing schedule
          path = '/api/v1/agents/scheduler/' + event.eventId + '/commands'
          data = action;
        } else {
          // assume this is a create and use the handy-dandy all-at-once route
          path = '/api/v1/agents/scheduler';
          event.command = action;
          event.creator = CW_CREATOR_ID;
          data = event;
          data.creatorState = this.utils.buildCreatorState(event, action);
        }

        if (path && data) {
          this.broker.call({
            method: 'POST',
            path: path,
            data: data
          }).then(res => {
            console.log('response from schedule event compound create', res);
            this.refreshEvent(res.eventId).then(res => {
              this.refresh();
              resolve(res);
            }, err => {
              reject(err);
            });
          }, err => {
            console.error('sched: error saving action', err);
            reject(err);
          });
        } else {
          reject({
            type: 'INTERNAL', scope: 'SCHEDULER', message: 'invalid state for saving schedule codeitem', context: { event: event, action: action }
          });
        }
      } else {
        reject({
          type: 'INTERNAL', scope: 'SCHEDULER', message: 'attempt to save schedule/action without valid event in context', context: { event: event, action: action }
        });
      }
    });
  }

  deleteAction(event: any, action: any): Promise<any> {
    console.log('sched: request to delete action', event, action);
    return new Promise<any>((resolve, reject) => {
      let state = JSON.parse(action.creatorState || '{}');
      let canDelete = (action.creator === CW_CREATOR_ID) || (!!state.tags && state.tags.indexOf(TAG_QSP) >= 0);

      console.log('sched: can delete action', canDelete);
      if (canDelete) {
        this.broker.call({
          method: 'DELETE',
          path: '/api/v1/agents/scheduler/' + event.eventId + '/' + action.codeItemId
        }).then(res => {
          this.refresh().then(res => resolve(res), err => reject(err));
        }, err => {
          console.error('sched: error from delete action', err);
          reject(err);
        });
      } else {
        console.error('sched: cannot delete action, protected');
        reject({ message: 'Cannot delete this action because it is protected.  Please contact your Control4 dealer for assistance.' });
      }
    });
  }

  deleteSchedule(event: Event): Promise<any> {
    this.store.dispatch({ type: SET_READY, payload: false });
    console.log('sched: request to delete schedule', event);
    return new Promise<any>((resolve, reject) => {
      if (event && event.deviceId === 100100 && event.eventId > 0) {
        // NOTE canDelete returns true by default
        let canDelete: boolean = this._canDelete(event.codeItems);
        console.log('sched: can delete schedule', canDelete);
        if (canDelete) {
          this.broker.call({
            path: '/api/v1/agents/scheduler/' + event.eventId,
            method: 'DELETE'
          }).then(res => {
            // console.log('sched: delete schedule response', res);
            // NOTE this will also set the ready flag
            this.refresh().then(res => resolve(res), err => reject(err));
          }, err => {
            console.error('sched: error deleting schedule', err);
            this.store.dispatch({ type: SET_READY, payload: true });
            reject({ message: 'error deleting schedule', err: err });
          });
        } else {
          console.error('sched: cannot delete action, protected');
          this.store.dispatch({ type: SET_READY, payload: true });
          reject({ message: 'cannot delete protected action' });
        }
      } else {
        console.error('sched: invalid schedule given in delete context', event);
        this.store.dispatch({ type: SET_READY, payload: true });
        reject({ message: 'invalid schedule given for delete' });
      }
    });
  }

  private _canDelete(codeitems: CodeItem[]): boolean {
    let canDelete = true;

    if (codeitems) {
      codeitems.forEach(ci => {
        canDelete = canDelete && (!ci.protected) && this._canDelete(ci.codeItems);
      });
    }

    return canDelete;
  }

  buildEvent(): ScheduleEvent {
    let context: ScheduleQuickstartContext = Utils.snapshot<ScheduleQuickstartContext>(this.store.select(s => s.scheduleProgramming));
    console.log('building schedule event from context: ', context);

    let event: ScheduleEvent = Object.assign({}, BLANK_EVENT);
    event.display = context.name;
    event.eventId = context.eventId || 0;

    if (context.repeatFrequency > 0) {
      event.repeat.rate = context.repeatRate;
      event.repeat.frequency = context.repeatFrequency;
      event.repeat.daymask = context.repeatDays;

      if (context.hasEndDate) {
        let parts = context.endDate.split('-');
        if (parts.length === 3) {
          event.repeat.end_date = {
            end_year: parseInt(parts[0]),
            end_month: parseInt(parts[1]),
            end_day: parseInt(parts[2])
          };
        } else {
          console.warn('invalid end date', parts);
        }
      }
    } else {
      delete event.repeat;
    }

    if (context.isFixedTime) {
      let parts = context.startTime.split(':');
      event.start.offset_minutes = (parseInt(parts[0]) * 60) + parseInt(parts[1]);
    } else {
      event.start.offset = context.sunriseOrSunset;
      event.start.offset_minutes = (context.sunriseSunsetOffsetType === 2) ?
        context.sunriseSunsetOffsetInterval : -(context.sunriseSunsetOffsetInterval);
    }

    if (context.isFixedDate) {
      let parts = context.startDate.split('-');
      event.start.start_date.start_year = parseInt(parts[0]);
      event.start.start_date.start_month = parseInt(parts[1]);
      event.start.start_date.start_day = parseInt(parts[2]);
      delete event.start.start_date.start_period;
      delete event.start.start_date.start_weekday;
    } else {
      event.start.start_date.start_year = context.startYear;
      event.start.start_date.start_month = context.startMonth;
      event.start.start_date.start_weekday = context.startDayOfWeek;
      event.start.start_date.start_period = context.startWeekOffset;
      delete event.start.start_date.start_day;
    }

    // compose a subtitle representing the start time
    event.subtitle = this.scheduleLabel.transform(event);

    console.log('sched: built schedule event', event);
    return event;
  }
}




// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/quickstarts/quickstart-schedule/services/schedule.service.ts